import { Component, computed, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { StatsCampaignType, StrategyStateEnum } from "@front/m19-api-client";
import { ACOS, AD_CONVERSIONS, AD_SALES, CLICKS, CONVERSION_RATE, COST, CPC, Metric, ROAS } from "@front/m19-metrics";
import { AdStatsEx, Marketplaces } from "@front/m19-models";
import { DefaultAdStatsMetricsWithSameScale } from "@front/m19-models/dataset/Dataset";
import { SpStrategyEx } from "@front/m19-models/strategy/SpStrategyEx";
import {
  AccountSelectionService,
  AuthService,
  MetricsSelectorPage,
  MetricsSelectorKey,
  SpStrategyService,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import {
  ChartConfig,
  IButtonDirective,
  IChartComponent,
  IDropdownMenuSeparatorComponent,
  IIconComponent,
  ISelectMenuImports,
  ITooltipDirective,
} from "@front/m19-ui";
import {
  Comparison,
  DateAggregation,
  DateUtils,
  divideAdStats,
  MapUtils,
  mergeSeveralDates,
  sumAdStats,
} from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ChartLayoutComponent } from "@m19-board/containers/chart-layout/chart-layout.component";
import { MetricSelectorComponent } from "@m19-board/insights/metric-selector/metric-selector.component";
import { StrategyStats } from "@m19-board/models/Metric";
import { UntilDestroy } from "@ngneat/until-destroy";
import { DateTime } from "luxon";
import { combineLatest, map, shareReplay, startWith, switchMap } from "rxjs";
import { HourlyTableComponent } from "../hourly-table/hourly-table.component";

interface HourlyData {
  avgChartData: Map<string, AdStatsEx>;
  hourlyAvgByStrategy: Map<StrategyIdOrNotOperated, StrategyStats[]>;
  globalData: AdStatsEx;
}

export const NOT_OPERATED = "NOT_OPERATED";
export const DELETED = "DELETED";
export type StrategyIdOrNotOperated = number | typeof NOT_OPERATED;

@UntilDestroy()
@Component({
  selector: "app-hourly-page",
  templateUrl: "./hourly-page.component.html",
  imports: [
    HourlyTableComponent,
    ChartLayoutComponent,
    MetricSelectorComponent,
    IChartComponent,
    IButtonDirective,
    IIconComponent,
    IDropdownMenuSeparatorComponent,
    ISelectMenuImports,
    ITooltipDirective,
    TranslocoDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HourlyPageComponent {
  private readonly authService = inject(AuthService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly accountSelection = inject(AccountSelectionService);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly statsService = inject(StatsService);
  private readonly translocoService = inject(TranslocoService);

  readonly selectedMetrics = signal<Metric<AdStatsEx>[]>([AD_SALES, COST]);
  readonly chartDisplayed = signal(true);
  readonly filterIds = signal<StrategyIdOrNotOperated[]>([]);
  readonly searchQuery = signal<string | undefined>("");

  readonly currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  private readonly dateRange = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });
  private readonly accountMarketplace = toSignal(this.accountSelection.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  readonly localStorageKey: MetricsSelectorKey = { page: MetricsSelectorPage.hourlyStats };
  readonly CHART_METRICS: Metric<AdStatsEx>[] = [
    AD_SALES,
    AD_CONVERSIONS,
    COST,
    ACOS,
    CLICKS,
    CPC,
    CONVERSION_RATE,
    ROAS,
  ];

  private readonly strategies$ = this.accountSelection.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.spStrategyService.getStrategies(am.accountId, am.marketplace)),
    shareReplay(1),
  );

  readonly strategies = toSignal(
    this.strategies$.pipe(map((s) => Array.from(s.values()).filter((x) => !x.isTacos()))),
    { initialValue: [] as SpStrategyEx[] },
  );

  readonly filteredStrategies = computed(() => {
    // filter strategy based on searchQuery
    const query = this.searchQuery()?.trim()?.toLocaleLowerCase();
    if (!query) return this.strategies();
    return this.strategies().filter((x) => x.name.toLowerCase().includes(query));
  });

  readonly filterDisplayLabel = computed(() => {
    const ids = this.filterIds();
    if (ids.length === 1) {
      if (ids[0] === NOT_OPERATED) return this.translocoService.translate("hourly-page.not_operated_campaigns");
      return this.strategies().find((s) => s.strategyId === ids[0])?.name ?? `${ids[0]}`;
    }
    if (ids.length === this.strategies().length + 1) {
      return this.translocoService.translate("strategy-list.all_strategies");
    }
    return this.translocoService.translate("imultiselect.option_selected", { count: ids.length });
  });

  private readonly rawHourlyData$ = combineLatest([
    this.accountSelection.singleAccountMarketplaceSelection$,
    this.userSelectionService.dateRange$,
    this.userSelectionService.selectedCurrency$,
  ]).pipe(
    switchMap(([am, dateRange, currency]) =>
      this.statsService.getHourlyCampaignStats(am.accountId, am.marketplace, currency, dateRange[0], dateRange[1]),
    ),
    shareReplay(1),
  );

  private readonly hourlyData$ = combineLatest([
    this.rawHourlyData$,
    this.strategies$,
    toObservable(this.filterIds),
  ]).pipe(
    map(([data, strategiesMap, filterIds]) => {
      const strategyIndex = new Map(
        Array.from(strategiesMap.values())
          .filter((x) => !x.isTacos())
          .map((x) => [x.strategyId!, x]),
      );
      return this.computePageData(data, strategyIndex, filterIds);
    }),
    shareReplay(1),
  );

  readonly chartData = toSignal(
    this.hourlyData$.pipe(
      map((d) => Array.from(d.avgChartData.values())),
      startWith([] as AdStatsEx[]),
    ),
    { requireSync: true },
  );

  readonly avgByTypeHour = toSignal(
    this.hourlyData$.pipe(
      map((d) => d.hourlyAvgByStrategy),
      startWith(new Map<StrategyIdOrNotOperated, StrategyStats[]>()),
    ),
    { requireSync: true },
  );

  readonly globalData = toSignal(this.hourlyData$.pipe(map((d) => d.globalData)));

  readonly chartConfig = computed<ChartConfig<AdStatsEx, "line">>(() => ({
    type: "line",
    aggFunctions: [mergeSeveralDates],
    aspectRatio: 3,
    metrics: this.selectedMetrics(),
    dateAggregation: DateAggregation.hourly,
    dateRange: this.dateRange(),
    metricsOnSameScale: DefaultAdStatsMetricsWithSameScale,
  }));

  readonly selectedTimeZone = computed(() => {
    const marketplace = Marketplaces[this.accountMarketplace().marketplace];
    return DateTime.local({ zone: marketplace.timeZone, locale: this.locale() });
  });

  readonly formattedTimeZone = computed(() => this.selectedTimeZone().toFormat("ZZZZ", { locale: this.locale() }));
  readonly timeZoneOffset = computed(() => DateUtils.getTimezoneOffset(this.selectedTimeZone().zoneName));

  constructor() {
    this.userSelectionService.togglePeriodComparison(undefined, Comparison.None);
    this.chartDisplayed.set(this.userSelectionService.getUserChartDisplayedPreference(this.localStorageKey));
  }

  setChartDisplayed(displayed: boolean): void {
    this.chartDisplayed.set(displayed);
    this.userSelectionService.setUserChartDisplayedPreference(this.localStorageKey, displayed);
  }

  private computePageData(
    data: AdStatsEx[],
    strategyIndex: Map<number, SpStrategyEx>,
    filterIds: StrategyIdOrNotOperated[],
  ): HourlyData {
    const filteredData = data.filter((row) => {
      if (!row.impressions) return false;
      if (filterIds.length === 0) return true;
      if (!row.strategyId) return filterIds.includes(NOT_OPERATED);
      return filterIds.includes(row.strategyId);
    });

    const dates = new Set<string>();
    const statsByHourByDate = new Map<string, AdStatsEx>();
    const allStatsByType = new Map<StrategyIdOrNotOperated, AdStatsEx[]>();

    for (const stat of filteredData) {
      dates.add(stat.date!);
      MapUtils.insertInMap(statsByHourByDate, `${stat.hour}_${stat.date}`, stat, sumAdStats);
      MapUtils.insertInArrayMap(allStatsByType, stat.strategyId ?? NOT_OPERATED, stat);
    }

    const nbDaysConsidered = dates.size;

    const avgChartData = MapUtils.sumBy(
      Array.from(statsByHourByDate.values()),
      (x) => x.hour?.toString() ?? "",
      sumAdStats,
    );
    avgChartData.forEach((value) => divideAdStats(value, nbDaysConsidered));

    let globalData: AdStatsEx = {};
    for (const stat of avgChartData.values()) globalData = mergeSeveralDates(globalData, stat);

    return {
      avgChartData,
      hourlyAvgByStrategy: this.computeAvgByTypeHour(allStatsByType, strategyIndex, nbDaysConsidered),
      globalData,
    };
  }

  private computeAvgByTypeHour(
    allStatsByType: Map<StrategyIdOrNotOperated, AdStatsEx[]>,
    strategyIndex: Map<number, SpStrategyEx>,
    nbDaysConsidered: number,
  ): Map<StrategyIdOrNotOperated, StrategyStats[]> {
    const result = new Map<StrategyIdOrNotOperated, StrategyStats[]>();

    allStatsByType.forEach((stats, key) => {
      const deduped = Array.from(MapUtils.sumBy(stats, (x) => `${x.date}_${x.hour}`, sumAdStats).values()).map((x) =>
        this.toStrategyStats(x, strategyIndex),
      );

      const avgList = Array.from(
        MapUtils.sumBy(deduped, (x) => x.hour?.toString() ?? "", sumAdStats).values(),
      ) as StrategyStats[];

      avgList.forEach((stat) => divideAdStats(stat, nbDaysConsidered));
      avgList.sort((a, b) => a.hour! - b.hour!);
      result.set(key, avgList);
    });

    return result;
  }

  private toStrategyStats(adStats: AdStatsEx, strategyIndex: Map<number, SpStrategyEx>): StrategyStats {
    const strategy = adStats.strategyId ? strategyIndex.get(adStats.strategyId) : undefined;
    return {
      ...adStats,
      strategyName: adStats.strategyId ? (strategy?.name ?? "Deleted strategy") : "Non-operated campaigns",
      acosTarget: strategy?.acosTarget ?? 0,
      state: strategy?.state ?? StrategyStateEnum.ENABLED,
      campaignType: adStats.campaignType ?? StatsCampaignType.SP,
      dayPartingEnabled: strategy?.daypartingPauseHour != null && strategy?.daypartingReactivationHour != null,
      dayPartingPauseHour: strategy?.daypartingPauseHour ?? null,
      dayPartingReactivationHour: strategy?.daypartingReactivationHour ?? null,
    };
  }
}
